
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SimulationConfig, BallPlacementConfig } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { 
  Plus, 
  Minus, 
  Move, 
  Maximize2, 
  Target, 
  Compass,
  Zap,
  Grid,
  Circle as CircleIcon,
  Square as SquareIcon,
  LayoutGrid
} from 'lucide-react';

import { CollapsibleSection } from './CollapsibleSection';

interface BallPlacementSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const BallPlacementSection: React.FC<BallPlacementSectionProps> = ({ 
  config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  const updateIndividualConfig = (index: number, updates: Partial<BallPlacementConfig>) => {
    setConfig(p => {
      const newConfigs = [...(p.ballPlacementConfigs || [])];
      if (!newConfigs[index]) {
        newConfigs[index] = {
          id: Math.random().toString(),
          mode: 'CENTER',
          posX: 0,
          posY: 0,
          border: 'TOP',
          placementAngle: 0,
          distance: 200,
          directionAngle: 0,
          speed: (p.initialSpeed * 12) || 12,
          randomDirection: p.ballPlacementRandomDirection || false
        };
      }
      newConfigs[index] = { ...newConfigs[index], ...updates };
      return { ...p, ballPlacementConfigs: newConfigs };
    });
  };

  return (
    <CollapsibleSection 
        title="Ball Initial State" 
        icon={Plus} 
        darkMode={config.darkMode} 
        settingKey="ballPlacement"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-8 mt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${config.darkMode ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/10 shadow-[0_0_15px_rgba(6,182,212,0.15)]' : 'bg-white shadow-sm border border-zinc-100'}`}>
                <Target className="w-5 h-5 text-cyan-600" />
            </div>
            <div className="flex flex-col">
                <span className="text-[12px] font-medium uppercase tracking-[0.3em] opacity-60">Physics Seed</span>
                <span className="text-[8px] font-medium text-zinc-400 uppercase tracking-widest mt-1">Starting parameters</span>
            </div>
        </div>

        <div className="space-y-10">
            {/* 1. Deployment Pattern */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                    <LayoutGrid className="w-3.5 h-3.5 opacity-20 text-cyan-600" />
                    <h4 className="text-[9px] font-medium uppercase tracking-[0.2em] opacity-30">Deployment Matrix</h4>
                    <InfoPopup content="" settingKey="ballPlacementMode" darkMode={config.darkMode} />
                </div>
                <div className={`grid grid-cols-2 gap-2 p-1.5 rounded-[1.8rem] border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-zinc-50 border-zinc-100 shadow-inner'}`}>
                    {(['CENTER', 'BORDER', 'ANGLE', 'MANUAL'] as const).map(m => (
                        <button
                            key={m}
                            onClick={() => setConfig(p => ({ ...p, ballPlacementMode: m }))}
                            className={`py-3 px-4 rounded-xl text-[9px] font-medium uppercase tracking-[0.15em] transition-all border ${
                                config.ballPlacementMode === m 
                                ? (config.darkMode ? 'bg-white shadow-lg text-zinc-950 border-white' : 'bg-white text-cyan-600 border-cyan-600/30 shadow-md') 
                                : 'text-zinc-400 hover:text-zinc-600 border-transparent'
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>

            {/* 2. Conditional Parameters */}
            <AnimatePresence mode="wait">
                <motion.div 
                    key={config.ballPlacementMode}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    className="space-y-6 pt-2"
                >
                    {config.ballPlacementMode === 'MANUAL' && (
                        <div className="grid grid-cols-2 gap-3">
                            <SmartInput label="Horizontal Pos" settingKey="ballPlacementPosX" min={-100} max={100} value={config.ballPlacementPosX} onChange={(v: number) => setConfig(p => ({ ...p, ballPlacementPosX: v }))} darkMode={config.darkMode} />
                            <SmartInput label="Vertical Pos" settingKey="ballPlacementPosY" min={-100} max={100} value={config.ballPlacementPosY} onChange={(v: number) => setConfig(p => ({ ...p, ballPlacementPosY: v }))} darkMode={config.darkMode} />
                        </div>
                    )}

                    {config.ballPlacementMode === 'BORDER' && (
                        <div className="flex flex-col gap-2">
                            <div className="flex justify-end"><InfoPopup content="" settingKey="ballPlacementBorder" darkMode={config.darkMode} /></div>
                            <div className="grid grid-cols-4 gap-2">
                                {(['TOP', 'BOTTOM', 'LEFT', 'RIGHT'] as const).map(b => (
                                <button
                                    key={b}
                                    onClick={() => setConfig(p => ({ ...p, ballPlacementBorder: b }))}
                                    className={`py-3 rounded-xl text-[9px] font-black uppercase border transition-all ${
                                        config.ballPlacementBorder === b 
                                        ? 'bg-neon-cyan text-white border-cyan-500 shadow-md' 
                                        : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500' : 'bg-white border-zinc-200 text-zinc-400 hover:bg-zinc-50')
                                    }`}
                                >
                                    {b.charAt(0)}
                                </button>
                            ))}
                            </div>
                        </div>
                    )}

                    {config.ballPlacementMode === 'ANGLE' && (
                        <div className="grid grid-cols-2 gap-3">
                            <SmartInput label="Position Angle" settingKey="ballPlacementAngle" min={0} max={360} value={config.ballPlacementAngle} onChange={(v: number) => setConfig(p => ({ ...p, ballPlacementAngle: v }))} darkMode={config.darkMode} />
                            <SmartInput label="Distance from Center" settingKey="ballPlacementDistance" min={0} max={100} value={config.ballPlacementDistance} onChange={(v: number) => setConfig(p => ({ ...p, ballPlacementDistance: v }))} darkMode={config.darkMode} />
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* 3. Trajectory Controls */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Compass className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Trajectory</h4>
                </div>
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <SmartInput label="Start Angle" settingKey="ballPlacementDirectionAngle" min={0} max={360} value={config.ballPlacementDirectionAngle} onChange={(v: number) => setConfig(p => ({ ...p, ballPlacementDirectionAngle: v }))} darkMode={config.darkMode} />
                        </div>
                        <ModifierCard 
                            label="Randomize" 
                            settingKey="ballPlacementRandomDirection"
                            active={config.ballPlacementRandomDirection} 
                            onToggle={() => setConfig(p => ({ ...p, ballPlacementRandomDirection: !p.ballPlacementRandomDirection }))} 
                            darkMode={config.darkMode}
                            icon={Zap}
                        />
                    </div>
                </div>
            </div>

            {/* 4. Granular Overrides */}
            <div className={`p-8 rounded-[2.5rem] border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-neon-cyan/20 border border-cyan-500/20 flex items-center justify-center">
                            <Settings2 className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black uppercase tracking-widest leading-none">Individual Ball Settings</span>
                            <span className="text-[8px] font-bold opacity-40 uppercase tracking-tight mt-1">Individual unit precision</span>
                        </div>
                        <InfoPopup content="" settingKey="ballPlacementIndividualEnabled" darkMode={config.darkMode} />
                    </div>
                    <Toggle active={config.ballPlacementIndividualEnabled} onClick={() => setConfig(p => ({ ...p, ballPlacementIndividualEnabled: !p.ballPlacementIndividualEnabled }))} />
                </div>

                <AnimatePresence>
                    {config.ballPlacementIndividualEnabled && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden space-y-4"
                        >
                            <div className="max-h-[300px] overflow-y-auto pr-3 space-y-3 custom-scrollbar">
                                {Array.from({ length: config.initialBalls || 1 }).map((_, i) => (
                                    <IndividualBallCard 
                                        key={i} 
                                        index={i} 
                                        config={config} 
                                        ballCfg={config.ballPlacementConfigs?.[i] || {}} 
                                        onUpdate={(updates: Partial<BallPlacementConfig>) => updateIndividualConfig(i, updates)} 
                                    />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
        </div>
    </CollapsibleSection>
  );
};

const ModifierCard = ({ label, active, onToggle, darkMode, icon: Icon, settingKey }: any) => (
    <div className={`relative flex items-center gap-3 h-14 p-3 rounded-2xl border transition-all duration-300 group ${active ? (darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-white border-zinc-950/10 text-zinc-900 shadow-sm') : 'bg-transparent border-transparent text-zinc-400'}`}>
        <button className="absolute inset-0 w-full h-full cursor-pointer z-0" onClick={onToggle} />
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all pointer-events-none z-10 ${active ? 'bg-neon-cyan text-white shadow-lg shadow-cyan-500/20' : 'bg-zinc-200 text-zinc-500 group-hover:bg-zinc-300'}`}>
            <Icon className="w-4 h-4" />
        </div>
        <div className="flex flex-col items-start pr-2 pointer-events-none z-10">
            <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-black uppercase tracking-tight leading-none">{label}</span>
            </div>
            <span className={`text-[7px] font-bold uppercase mt-1 ${active ? 'text-blue-500' : 'text-zinc-400'}`}>{active ? 'Active' : 'Off'}</span>
        </div>
        {settingKey && <div className="absolute right-2 top-2 z-20"><InfoPopup content="" settingKey={settingKey} darkMode={darkMode} /></div>}
    </div>
);

const IndividualBallCard = ({ index, config, ballCfg, onUpdate }: any) => (
    <div className={`p-4 rounded-3xl border ${config.darkMode ? 'bg-black/30 border-white/5' : 'bg-white border-zinc-200 shadow-sm'}`}>
        <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] font-black uppercase text-blue-500">Unit #{index + 1}</span>
            <div className="flex items-center gap-2">
                <button 
                    onClick={() => onUpdate({ posX: 0, posY: 0 })}
                    className="text-[8px] font-black uppercase opacity-30 hover:opacity-100 transition-opacity"
                >
                    Reset Pos
                </button>
            </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
            <SmartInput label="Horizontal" min={-100} max={100} value={ballCfg.posX || 0} onChange={(v: number) => onUpdate({ posX: v })} darkMode={config.darkMode} />
            <SmartInput label="Vertical" min={-100} max={100} value={ballCfg.posY || 0} onChange={(v: number) => onUpdate({ posY: v })} darkMode={config.darkMode} />
            <SmartInput label="Speed" min={0} max={100} value={ballCfg.speed || 10} onChange={(v: number) => onUpdate({ speed: v })} darkMode={config.darkMode} />
            <SmartInput label="Angle" min={0} max={360} value={ballCfg.directionAngle || 0} onChange={(v: number) => onUpdate({ directionAngle: v })} darkMode={config.darkMode} />
        </div>
    </div>
);

import { Settings2, Info } from 'lucide-react';
import { InfoPopup } from './InfoPopup';
